import React, { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Flex } from "../../../../components/Flex";
import { Spacing } from "../../../../theme/Spacing";
import { CreateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/CreateModuleDefinitionDto";
import { UpdateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/UpdateModuleDefinitionDto";
import { ModuleDefinitionTemplates } from "../../../../model/module/types/module-definition/ModuleDefinitionTemplates";
import { Clickable } from "../../../../components/Clickable";
import { ModuleDefinitionKind } from "../../../../model/module/types/module-definition/ModuleDefinitionKind";
import { Text } from "../../../../components/Text";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { faLockOpen } from "@fortawesome/free-solid-svg-icons/faLockOpen";
import { faPuzzlePiece } from "@fortawesome/free-solid-svg-icons/faPuzzlePiece";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { useStepperRegisterValidation } from "../../../../components/stepper/useStepperRegisterValidation";
import { useStepContext } from "../../../../components/stepper/StepperContext";
import { Rounded } from "../../../../theme/Rounded";
import { TestIDs } from "../../../../tests/types/testIds";

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xl};
  align-self: flex-start;
`;

const StyledClickable = styled(Clickable)<{ selected: boolean }>`
  border: 1px solid ${({ theme }) => theme.sub};
  border-radius: ${Rounded.md};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl};
  outline: ${({ selected, theme }) =>
    selected ? `2px solid ${theme.main}` : `1px solid ${theme.sub}`};
  padding: ${Spacing.l};
  max-width: 25rem;
  flex-grow: 1;
  box-shadow: ${({ selected, theme }) =>
    selected ? `0 0 10px ${theme.main}33` : "none"};
  box-sizing: border-box;
  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    box-shadow: ${({ theme }) => `0 0 10px ${theme.main}33`};
  }
`;

export const ModuleDefinitionCreateEditConfigStep: React.FC = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "modules.definition.kind",
  });

  const { control } = useFormContext<
    (CreateModuleDefinitionDto | UpdateModuleDefinitionDto) & {
      template?: ModuleDefinitionTemplates;
    }
  >();

  const { currentStep } = useStepContext();
  const { watch, getValues, setValue } = useFormContext<
    (CreateModuleDefinitionDto | UpdateModuleDefinitionDto) & {
      template?: ModuleDefinitionTemplates | null;
    }
  >();
  const kind = watch("kind");

  const canGoNext = useCallback(() => {
    return kind?.length > 0;
  }, [kind]);

  const onSelectKind = (
    kind: ModuleDefinitionKind,
    onChange: ATVoidFunction<ModuleDefinitionKind>,
  ) => {
    onChange(kind);

    if (
      kind === ModuleDefinitionKind.FIXED &&
      getValues("template") === ModuleDefinitionTemplates.NOTE
    ) {
      setValue("template", null);
    }
  };

  useStepperRegisterValidation(currentStep, canGoNext);

  return (
    <Container>
      <Flex alignItems="center" gap="s">
        <AtiraIcon icon={faQuestionCircle} color="main" size="xl" />
        <Text fontSize="l" fontWeight={"bold"} color="main">
          {t("title")}
        </Text>
      </Flex>
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <Flex gap="m" justifyContent="space-between" width={"100%"}>
            {Object.keys(ModuleDefinitionKind).map((key) => (
              <StyledClickable
                key={key}
                onClick={() =>
                  onSelectKind(key as ModuleDefinitionKind, onChange)
                }
                selected={value === key}
                dataTestId={TestIDs.modules.createEdit.stepKind.kind(key)}
              >
                <Flex flexDirection="column" gap="m">
                  <Text fontWeight={"bold"}>
                    {t(`${key.toLowerCase()}.title`)}
                  </Text>
                  <Text color="darkerSub">
                    {t(`${key.toLowerCase()}.description`)}
                  </Text>
                </Flex>

                <Flex gap="s" alignItems="center" justifyContent="center">
                  <AtiraIcon
                    size="2xl"
                    icon={
                      key === ModuleDefinitionKind.FIXED ? faLockOpen : faLock
                    }
                    color="main"
                  />
                  <AtiraIcon size="2xl" icon={faPuzzlePiece} color="main" />
                </Flex>
              </StyledClickable>
            ))}
          </Flex>
        )}
        name={"kind"}
      />
    </Container>
  );
};
